import { agpAxiosClient } from '../../Common/api/ABHub/ABHubTokenConfig';
import { CancelToken } from 'axios';
import {
    getConfig,
    GlobalConfigProperties,
} from '../../Common/api/ABHub/ABHubGlobalConfig';
import {
    CloudStatusResponse,
    CloudStatusApiRequest,
    CloudStatusListResponse,
    ComponentEventsResponse,
    ComponentDetailsResponse,
} from '../models/CloudStatusApiModels';

/**
 * Get cloud status information
 * @param request Optional parameters to filter and customize the response
 * @param cancelToken Optional axios cancel token
 * @returns Promise containing cloud status data
 */
export const getCloudStatus = async (
    request?: CloudStatusApiRequest,
    cancelToken?: CancelToken
): Promise<CloudStatusResponse> => {
    try {
        // Hardcoded for now - can be replaced with getConfig(GlobalConfigProperties.SdsUrl) later
        const sdsUrl = 'https://localhost:5010/api/sds';

        const params: Record<string, any> = {};

        if (request) {
            if (request.cloudName) {
                params.cloudName = request.cloudName;
            }
            if (request.includeServices !== undefined) {
                params.includeServices = request.includeServices;
            }
            if (request.includePipelines !== undefined) {
                params.includePipelines = request.includePipelines;
            }
            if (request.includeEvents !== undefined) {
                params.includeEvents = request.includeEvents;
            }
            if (request.maxEventAgeHours !== undefined) {
                params.maxEventAgeHours = request.maxEventAgeHours;
            }
        }

        const response = await agpAxiosClient.get<CloudStatusResponse>(
            `${sdsUrl}/cloudstatus`,
            {
                params,
                cancelToken,
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error fetching cloud status:', error);

        // Return error response structure
        return {
            clouds: [],
            timestamp: new Date().toISOString(),
            success: false,
            errorMessage:
                (error as any).response?.data?.errorMessage ||
                (error as any).message ||
                'Failed to fetch cloud status',
        };
    }
};

/**
 * Get cloud status for a specific cloud
 * @param cloudName The name of the cloud (e.g., "USSec", "USNat", "Public")
 * @param includeServices Whether to include service information
 * @param includePipelines Whether to include pipeline information
 * @param includeEvents Whether to include event history
 * @param cancelToken Optional axios cancel token
 * @returns Promise containing cloud status data
 */
export const getCloudStatusByName = async (
    cloudName: string,
    includeServices: boolean = true,
    includePipelines: boolean = true,
    includeEvents: boolean = false,
    cancelToken?: CancelToken
): Promise<CloudStatusResponse> => {
    return getCloudStatus(
        {
            cloudName,
            includeServices,
            includePipelines,
            includeEvents,
        },
        cancelToken
    );
};

/**
 * Get all cloud statuses
 * @param includeServices Whether to include service information
 * @param includePipelines Whether to include pipeline information
 * @param includeEvents Whether to include event history
 * @param cancelToken Optional axios cancel token
 * @returns Promise containing all cloud status data
 */
export const getAllCloudStatuses = async (
    includeServices: boolean = true,
    includePipelines: boolean = true,
    includeEvents: boolean = false,
    cancelToken?: CancelToken
): Promise<CloudStatusResponse> => {
    return getCloudStatus(
        {
            includeServices,
            includePipelines,
            includeEvents,
        },
        cancelToken
    );
};

/**
 * Get a lightweight list of available clouds
 * @param cancelToken Optional axios cancel token
 * @returns Promise containing list of cloud summaries
 */
export const getCloudList = async (
    cancelToken?: CancelToken
): Promise<CloudStatusListResponse> => {
    try {
        // Hardcoded for now - can be replaced with getConfig(GlobalConfigProperties.SdsUrl) later
        const sdsUrl = 'https://localhost:5010/api/sds';

        const response = await agpAxiosClient.get<CloudStatusListResponse>(
            `${sdsUrl}/cloudstatus/list`,
            {
                cancelToken,
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error fetching cloud list:', error);

        // Return error response structure
        return {
            clouds: [],
            timestamp: new Date().toISOString(),
            success: false,
            errorMessage:
                (error as any).response?.data?.errorMessage ||
                (error as any).message ||
                'Failed to fetch cloud list',
        };
    }
};

/**
 * Get events for a specific component
 * @param componentGuid The component GUID to get events for
 * @param maxEventAgeHours Maximum age of events to include in hours
 * @param maxEvents Maximum number of events to return
 * @param cancelToken Optional axios cancel token
 * @returns Promise containing component events
 */
export const getComponentEvents = async (
    componentGuid: string,
    maxEventAgeHours: number = 24,
    maxEvents: number = 50,
    cancelToken?: CancelToken
): Promise<ComponentEventsResponse> => {
    try {
        // Hardcoded for now - can be replaced with getConfig(GlobalConfigProperties.SdsUrl) later
        const sdsUrl = 'https://localhost:5010/api/sds';

        const params: Record<string, any> = {
            maxEventAgeHours,
            maxEvents,
        };

        const response = await agpAxiosClient.get<ComponentEventsResponse>(
            `${sdsUrl}/events/component/${encodeURIComponent(componentGuid)}`,
            {
                params,
                cancelToken,
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error fetching component events:', error);

        // Return error response structure
        return {
            componentGuid,
            events: [],
            timestamp: new Date().toISOString(),
            success: false,
            errorMessage:
                (error as any).response?.data?.errorMessage ||
                (error as any).message ||
                'Failed to fetch component events',
        };
    }
};

/**
 * Get detailed information about a specific component
 * @param componentGuid The component GUID to get details for
 * @param includeContext Include the context dictionary
 * @param includeActions Include available actions
 * @param cancelToken Optional axios cancel token
 * @returns Promise containing component details
 */
export const getComponentDetails = async (
    componentGuid: string,
    includeContext: boolean = true,
    includeActions: boolean = true,
    cancelToken?: CancelToken
): Promise<ComponentDetailsResponse> => {
    try {
        // Hardcoded for now - can be replaced with getConfig(GlobalConfigProperties.SdsUrl) later
        const sdsUrl = 'https://localhost:5010/api/sds';

        const params: Record<string, any> = {
            includeContext,
            includeActions,
        };

        const response = await agpAxiosClient.get<ComponentDetailsResponse>(
            `${sdsUrl}/component/${encodeURIComponent(componentGuid)}`,
            {
                params,
                cancelToken,
            }
        );

        return response.data;
    } catch (error) {
        console.error('Error fetching component details:', error);

        // Return error response structure
        return {
            componentGuid,
            componentName: '',
            serviceCategory: '',
            pipelineStage: '',
            enabled: false,
            context: {},
            availableActions: [],
            timestamp: new Date().toISOString(),
            success: false,
            errorMessage:
                (error as any).response?.data?.errorMessage ||
                (error as any).message ||
                'Failed to fetch component details',
        };
    }
};

